import React, { useState } from "react";
import { getCollaborateurs, updateRolesProjet } from "../services/api";
import SelecteurPersonnes from "./SelecteurPersonnes";
import { LIBELLES_ROLE_PROJET, nomCollaborateur } from "../services/projet";

// ===================================================================
// The Crowd v2 — Fiche projet : equipe
//
// Quatre roles : chef (manager ou directeur), acteur principal,
// co-chefs, acteurs secondaires. Une personne n'a qu'un role ; le chef
// ne cumule pas avec l'acteur principal, sauf un directeur. Le backend
// verifie tout et renvoie un message clair en cas de refus.
// Modification par le chef et les co-chefs, enregistree d'un bloc
// (PUT /api/projets/{id}/roles).
//
// Props : projet, collaborateurId, droits, onModifie
// ===================================================================

const ROLES_CHEF = ["manager", "directeur"];

function versPersonne(c) {
  return { id: c.id, displayName: nomCollaborateur(c), email: c.email };
}

function ListeRole({ libelle, personnes }) {
  return (
    <div className="v2-projet-role">
      <span className="v2-label">{libelle}</span>
      {personnes.length === 0 ? (
        <span className="v2-vide">—</span>
      ) : (
        <ul className="v2-personnes-choix">
          {personnes.map((p) => (
            <li key={p.id} className="v2-pastille v2-projet-pastille">{p.nom}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProjetEquipeV2({ projet, collaborateurId, droits, onModifie }) {
  const [edition, setEdition] = useState(false);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [chefId, setChefId] = useState("");
  const [principalId, setPrincipalId] = useState("");
  const [cochefs, setCochefs] = useState([]);
  const [secondaires, setSecondaires] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const acteurs = projet.acteurs || [];
  const parRole = (role) => acteurs.filter((a) => a.role_projet === role);

  const ouvrir = async () => {
    setErreur(null);
    try {
      const liste = await getCollaborateurs();
      setCollaborateurs(liste);
      const parId = Object.fromEntries(liste.map((c) => [c.id, c]));
      const personnes = (role) => parRole(role).map((a) => (parId[a.id] ? versPersonne(parId[a.id]) : { id: a.id, displayName: a.nom, email: `id-${a.id}` }));
      setChefId(String(projet.chef_projet_id || ""));
      setPrincipalId(String(projet.acteur_principal_id || ""));
      setCochefs(personnes("cochef"));
      setSecondaires(personnes("secondaire"));
      setEdition(true);
    } catch (err) {
      setErreur(`Collaborateurs non chargés : ${err.message}`);
    }
  };

  // Recherche locale dans les collaborateurs actifs (nom, prenom, adresse)
  const rechercher = async (texte) => {
    const t = texte.trim().toLowerCase();
    if (t.length < 2) return [];
    return collaborateurs
      .filter((c) => `${c.prenom} ${c.nom} ${c.email}`.toLowerCase().includes(t))
      .slice(0, 8)
      .map(versPersonne);
  };

  const enregistrer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await updateRolesProjet(projet.id, {
        chef_projet_id: Number(chefId),
        acteur_principal_id: Number(principalId),
        cochef_ids: cochefs.map((p) => p.id),
        secondaire_ids: secondaires.map((p) => p.id),
      }, collaborateurId);
      if (onModifie) await onModifie();
      setEdition(false);
    } catch (err) {
      setErreur(err.message);
    } finally {
      setEnCours(false);
    }
  };

  if (!edition) {
    return (
      <section className="v2-panneau v2-projet-bloc">
        <div className="v2-bloc-entete">
          <h2 className="v2-bloc-titre">Équipe</h2>
          {droits?.peut_gerer && (
            <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={ouvrir}>Modifier</button>
          )}
        </div>
        {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
        <div className="v2-projet-roles">
          <ListeRole libelle={LIBELLES_ROLE_PROJET.chef}
            personnes={projet.chef_projet_id ? [{ id: projet.chef_projet_id, nom: projet.chef_projet_nom }] : []} />
          <ListeRole libelle={LIBELLES_ROLE_PROJET.principal}
            personnes={projet.acteur_principal_id ? [{ id: projet.acteur_principal_id, nom: projet.acteur_principal_nom }] : []} />
          <ListeRole libelle="Co-chefs" personnes={parRole("cochef")} />
          <ListeRole libelle="Acteurs secondaires" personnes={parRole("secondaire")} />
        </div>
      </section>
    );
  }

  const chefsPossibles = collaborateurs.filter((c) => ROLES_CHEF.includes(c.role));

  return (
    <section className="v2-panneau v2-projet-bloc">
      <div className="v2-bloc-entete">
        <h2 className="v2-bloc-titre">Équipe</h2>
      </div>
      <div className="v2-formulaire">
        {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
        <div className="v2-champs-ligne">
          <div className="v2-champ">
            <label className="v2-label" htmlFor="equipe-chef">{LIBELLES_ROLE_PROJET.chef}<span className="v2-requis">*</span></label>
            <select id="equipe-chef" value={chefId} onChange={(e) => setChefId(e.target.value)}>
              <option value="">—</option>
              {chefsPossibles.map((c) => <option key={c.id} value={c.id}>{nomCollaborateur(c)}</option>)}
            </select>
            <span className="v2-champ-aide">Un manager ou un directeur.</span>
          </div>
          <div className="v2-champ">
            <label className="v2-label" htmlFor="equipe-principal">{LIBELLES_ROLE_PROJET.principal}<span className="v2-requis">*</span></label>
            <select id="equipe-principal" value={principalId} onChange={(e) => setPrincipalId(e.target.value)}>
              <option value="">—</option>
              {collaborateurs.map((c) => <option key={c.id} value={c.id}>{nomCollaborateur(c)}</option>)}
            </select>
            <span className="v2-champ-aide">La personne à qui l'objectif est affecté.</span>
          </div>
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="equipe-cochefs">Co-chefs</label>
          <SelecteurPersonnes id="equipe-cochefs" selection={cochefs} onChange={setCochefs} rechercher={rechercher}
            placeholder="Rechercher un collaborateur…" />
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="equipe-secondaires">Acteurs secondaires</label>
          <SelecteurPersonnes id="equipe-secondaires" selection={secondaires} onChange={setSecondaires} rechercher={rechercher}
            placeholder="Rechercher un collaborateur…" />
          <span className="v2-champ-aide">Ils consultent le projet, participent à la conversation et ajoutent ses étapes à leur saisie.</span>
        </div>
        <div className="v2-actions">
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setEdition(false)} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={enregistrer} disabled={enCours || !chefId || !principalId}>
            {enCours ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </section>
  );
}

export default ProjetEquipeV2;
